import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import Header from "../Header";
import Footer from "../Footer";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { provider, auth } from "../../../../firebase.js";
import { signInWithPopup, sendPasswordResetEmail, confirmPasswordReset } from "firebase/auth";
import axios from "../../../../libs/axios";
import googleIcon from "../../../../assets/google.svg";
import { UserDispatchContext, useUpgradeModal } from "../../../context/user.js";
import { useAuth } from "../../../context/auth.js";
import { getDaysDiff } from "../../../../libs/utility.js";
import "./Login.css";

const LogIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isFirebaseUser, setIsFirebaseUser] = useState(null);
  const [createPassword, setCreatePassword] = useState("");
  const { setIsAuthenticated } = useAuth();
  const { setshowupgrade } = useUpgradeModal();
  const setUser = useContext(UserDispatchContext);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [showAlreadyRegisteredDialog, setShowAlreadyRegisteredDialog] = useState(false);

  // Forgot Password States
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Password Reset States (for handling reset link)
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Store last verified email
  const [emailChecked, setEmailChecked] = useState("");

  const emailRegex = useMemo(
    () =>
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    []
  );

  // Check if user came from password reset email
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');
    
    if (mode === 'resetPassword' && oobCode) {
      setResetCode(oobCode);
      setShowPasswordResetDialog(true);
    }
  }, []);

  const getUser = (isLoggedIn) => {
    if (isLoggedIn) {
      axios.get("/api/user", { WithCredentials: true }).then((res) => {
        if (res.status === 200) {
          if (res.data.data) {
            setUser(res.data.data);
            const daysDiff = getDaysDiff(
              new Date(res.data.data?.createdAt),
              new Date()
            );
            const daysLeft = 15 - daysDiff;

            daysLeft > 0 && res.data.data?.subscription == null
              ? console.log(`${daysLeft} days free trial left`)
              : res.data.data?.subscription == null
                ? setshowupgrade(true)
                : new Date(res.data.data?.subscription.endDate) >= new Date()
                  ? console.log("Subscribed")
                  : console.log("Subscription Expired");
          }
        }
      });
    }
  };

  const loginCalling = async () => {
    try {
      setLoading(true);
      const loginUserURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/newauth/login`;

      const body = {
        email: email,
        password: password,
      };

      try {
        const { data } = await axios.post(loginUserURL, body);
        if (data?.success) {
          getUser(true);
          if (data.signInData.token) {
            setIsAuthenticated(true);
          }

          localStorage.setItem("token", data.signInData?.token);
          localStorage.setItem("userEmail", data.signInData?.email);
          localStorage.setItem("emailVerified", data.signInData?.emailVerified);
          localStorage.setItem("expiresIn", data.signInData?.expiresIn);
          localStorage.setItem("isAnonymous", data.signInData?.isAnonymous);
          localStorage.setItem("operationType", data.signInData?.operationType);
          localStorage.setItem("refreshToken", data.signInData?.refreshToken);
          localStorage.setItem("registered", data.signInData?.registered);
          navigate("/dashboard");
        } else {
          toast.error("Email/Password is not correct");
        }
      } catch (error) {
        console.error("Error during login request:", error);
        toast.error("Login failed");
      } finally {
        setLoading(false);
      }
    } catch (error) {
      toast.error(error);
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const googleLoginData = await signInWithPopup(auth, provider);
      if (googleLoginData.user) {
        const loginUserURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/newauth/login/google`;

        const body = {
          email: googleLoginData?.user?.email,
          uid: googleLoginData?.user?.uid,
          accessToken: googleLoginData?.user?.accessToken,
          userData: googleLoginData?.user?.reloadUserInfo,
          fullname: googleLoginData?.user?.displayName,
        };

        try {
          const { data } = await axios.post(loginUserURL, body, {
            WithCredentials: true,
          });
          if (data?.success) {
            getUser(true);
            if (data.signInData.token) {
              setIsAuthenticated(true);
            }
            localStorage.setItem("token", data.signInData?.token);
            localStorage.setItem("userEmail", googleLoginData?.user?.email);
            localStorage.setItem(
              "emailVerified",
              googleLoginData?.user?.emailVerified
            );
            localStorage.setItem(
              "operationType",
              data.signInData?.operationType
            );
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Error during login request:", error);
        }
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  const checkFirebaseUser = useCallback(async () => {
    if (!isValidEmail || !email) {
      toast.error("Please enter a valid email address");
      return false;
    }

    setIsCheckingUser(true);
    const isUserURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/newauth/isfirebaseuser`;

    const body = {
      email: email,
    };

    try {
      const { data } = await axios.post(isUserURL, body);
      if (data) {
        if (data?.success) {
          setIsFirebaseUser(true);
          setEmailChecked(email); // store checked email
          return true;
        } else {
          setIsFirebaseUser(false);
          setEmailChecked(email); // store checked email
          setShowAlreadyRegisteredDialog(true);
          return false;
        }
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setShowAlreadyRegisteredDialog(true);
        return false;
      }
      toast.error("Email is not authorized");
      return false;
    } finally {
      setIsCheckingUser(false);
    }
  }, [email, isValidEmail]);

  const handleNextClick = async () => {
    await checkFirebaseUser();
  };

  const createPawdHandler = async () => {
    try {
      setLoading(true);
      const loginUserURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/newauth/oldusersignup`;

      const body = {
        email: email,
        password: createPassword,
      };

      try {
        const { data } = await axios.post(loginUserURL, body);
        if (data?.success) {
          localStorage.setItem("token", data.signInData?.token);
          localStorage.setItem("userEmail", data.signInData?.email);
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error during login request:", error);
        toast.error("Failed to create password");
      } finally {
        setLoading(false);
      }
    } catch (error) {
      toast.error(error);
      setLoading(false);
    }
  };

  // Forgot Password Functions
  const handleForgotPasswordClick = () => {
    setForgotPasswordEmail(email || "");
    setShowForgotPasswordDialog(true);
    setResetEmailSent(false);
  };

  const handleSendResetEmail = async () => {
    if (!forgotPasswordEmail || !emailRegex.test(forgotPasswordEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSendingResetEmail(true);

    try {
      // First check if user exists in your backend
      const checkUserURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/newauth/isfirebaseuser`;
      const checkResponse = await axios.post(checkUserURL, {
        email: forgotPasswordEmail,
      });

      if (!checkResponse.data?.success) {
        toast.error("No account found with this email address");
        setIsSendingResetEmail(false);
        return;
      }

      // Send password reset email through Firebase
      await sendPasswordResetEmail(auth, forgotPasswordEmail, {
        url: window.location.origin + '/login', // Redirect back to login after reset
        handleCodeInApp: false
      });
      
      setResetEmailSent(true);
      toast.success("Password reset email sent successfully!");
      
    } catch (error) {
      console.error("Error sending reset email:", error);
      
      if (error.code === 'auth/user-not-found') {
        toast.error("No account found with this email address");
      } else if (error.code === 'auth/invalid-email') {
        toast.error("Invalid email address");
      } else if (error.code === 'auth/too-many-requests') {
        toast.error("Too many requests. Please try again later");
      } else {
        toast.error("Failed to send reset email. Please try again");
      }
    } finally {
      setIsSendingResetEmail(false);
    }
  };

  // Handle password reset from email link
  const handlePasswordReset = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error("Please fill in both password fields");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsResettingPassword(true);

    try {
      // Confirm password reset with Firebase
      await confirmPasswordReset(auth, resetCode, newPassword);
      
      toast.success("Password reset successfully! You can now log in with your new password.");
      setShowPasswordResetDialog(false);
      
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
    } catch (error) {
      console.error("Error resetting password:", error);
      
      if (error.code === 'auth/invalid-action-code') {
        toast.error("Invalid or expired reset link");
      } else if (error.code === 'auth/weak-password') {
        toast.error("Password is too weak");
      } else {
        toast.error("Failed to reset password. Please try again.");
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCloseForgotPasswordDialog = () => {
    setShowForgotPasswordDialog(false);
    setForgotPasswordEmail("");
    setResetEmailSent(false);
  };

  const handleClosePasswordResetDialog = () => {
    setShowPasswordResetDialog(false);
    setNewPassword("");
    setConfirmNewPassword("");
    setResetCode("");
    // Clear the URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Function to reset all states when email changes
  const resetFormStates = useCallback(() => {
    setIsFirebaseUser(null);
    setPassword("");
    setCreatePassword("");
    setEmailChecked("");
    setShowAlreadyRegisteredDialog(false);
  }, []);

  // Function to handle dialog close
  const handleDialogClose = useCallback(() => {
    setShowAlreadyRegisteredDialog(false);
    // Reset states when closing the dialog
    resetFormStates();
  }, [resetFormStates]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (email && emailRegex.test(email)) {
        setIsValidEmail(true);
      } else {
        setIsValidEmail(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [email, emailRegex]);

  const isDisabled = useMemo(() => {
    return (
      (isFirebaseUser && !password) || (!isFirebaseUser && !createPassword)
    );
  }, [isFirebaseUser, password, createPassword]);

  const buttonText =
    isFirebaseUser === null
      ? "Next"
      : isFirebaseUser
        ? "Login"
        : "Create Password";

  return (
    <>
      <Header />
      <div className="login-container">
        <form className="login-form">
          <div className="login-header">
            Welcome back to Oyster
          </div>

          {/* Google Login Button */}
          <button
            type="button"
            className="login-with-google-btn"
            onClick={loginWithGoogle}
          >
            <img
              src={googleIcon}
              alt="Google"
              className="google-icon"
            />
            <span>LOG IN WITH GOOGLE</span>
          </button>

          <div className="or">-OR-</div>

          {/* Email Field */}
          <div className="form-label">
            <InputLabel
              sx={{
                color: "#7C838A",
                fontFamily: "Poppins",
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: "500",
              }}
              htmlFor="email"
            >
              Email
            </InputLabel>
            <TextField
              sx={{
                width: "100%",
                height: "100%",
                background: "rgba(176, 186, 195, 0.40)",
                borderRadius: "8px",
              }}
              id="email"
              type="email"
              fullWidth
              margin="normal"
              required
              placeholder="Enter your Email here"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Reset states when email changes
                if (isFirebaseUser !== null) {
                  resetFormStates();
                }
              }}
            />
          </div>

          {/* Password Field for Firebase Users */}
          {isFirebaseUser === true && (
            <>
              <div className="form-label">
                <InputLabel
                  sx={{
                    color: "#7C838A",
                    fontFamily: "Poppins",
                    fontSize: "20px",
                    fontStyle: "normal",
                    fontWeight: "500",
                  }}
                  htmlFor="password"
                >
                  Password
                </InputLabel>
                <TextField
                  sx={{
                    width: "100%",
                    height: "100%",
                    background: "rgba(176, 186, 195, 0.40)",
                    borderRadius: "8px",
                  }}
                  id="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  margin="normal"
                  required
                  placeholder="Enter your Password here"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div
                className="remember-forgot-container"
                style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  width: "100%" 
                }}
              >
                <FormControlLabel
                  control={<Checkbox color="primary" size="small" />}
                  label={
                    <span
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "13px",
                        color: "#616161",
                      }}
                    >
                      Remember Me
                    </span>
                  }
                />
                <div className="forgot-password">
                  <button
                    type="button"
                    onClick={handleForgotPasswordClick}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#3B82F6",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: "500",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                    onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                    onMouseOut={(e) => e.target.style.textDecoration = "none"}
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Create Password Field for Non-Firebase Users */}
          {isFirebaseUser === false && (
            <div className="form-label">
              <InputLabel
                sx={{
                  color: "#7C838A",
                  fontFamily: "Poppins",
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: "500",
                }}
                htmlFor="createPassword"
              >
                Create Password
              </InputLabel>
              <TextField
                sx={{
                  width: "100%",
                  height: "100%",
                  background: "rgba(176, 186, 195, 0.40)",
                  borderRadius: "8px",
                }}
                id="createPassword"
                type={showCreatePassword ? "text" : "password"}
                fullWidth
                margin="normal"
                required
                placeholder="Create your Password here"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCreatePassword(!showCreatePassword)}
                        edge="end"
                      >
                        {showCreatePassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}

          {/* Main Action Button */}
          <div>
            <Button
              sx={{
                width: "100%",
                background: "#6B6B6B",
                borderRadius: "8px",
                color: "white",
                fontSize: 20,
                fontFamily: "Poppins",
                fontWeight: "400",
              }}
              variant="contained"
              color="primary"
              className="login-button"
              onClick={() => {
                if (isFirebaseUser === null) {
                  handleNextClick();
                } else {
                  // Check if email changed after verification
                  if (email !== emailChecked) {
                    toast.error("No account found with this email.");
                    return;
                  }

                  if (isFirebaseUser === true) {
                    loginCalling();
                  } else if (isFirebaseUser === false) {
                    createPawdHandler();
                  }
                }
              }}
              disabled={
                (isFirebaseUser === null && !isValidEmail) ||
                isCheckingUser ||
                (isFirebaseUser !== null && isDisabled) ||
                loading
              }
            >
              {isCheckingUser || loading ? (
                <CircularProgress size={24} sx={{ color: "#ffffff" }} />
              ) : (
                buttonText
              )}
            </Button>
          </div>

          {/* Sign Up Link */}
          <div className="signup-section">
            <span className="signup-text">Don't have an account? </span>
            <Link to="/new-sign-up" className="signup-link">
              SIGN UP
            </Link>
          </div>
        </form>
      </div>

      {/* Already Registered Dialog */}
      <Dialog
        open={showAlreadyRegisteredDialog}
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          Account Not Registered
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'Poppins, sans-serif' }}>
            This email is not registered. Please sign up first.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDialogClose}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: '#6B6B6B'
            }}
          >
            OK
          </Button>
          <Button
            onClick={() => navigate("/new-sign-up")}
            variant="contained"
            sx={{
              background: '#3B82F6',
              fontFamily: 'Poppins, sans-serif',
              borderRadius: '8px',
              textTransform: 'none'
            }}
          >
            Sign Up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog
        open={showForgotPasswordDialog}
        onClose={handleCloseForgotPasswordDialog}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          {resetEmailSent ? 'Reset Email Sent!' : 'Reset Password'}
        </DialogTitle>
        <DialogContent>
          {resetEmailSent ? (
            <DialogContentText sx={{ fontFamily: 'Poppins, sans-serif' }}>
              We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>. 
              Please check your email and click the link to reset your password.
              <br /><br />
              If you don't see the email, please check your spam folder.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ fontFamily: 'Poppins, sans-serif', marginBottom: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="forgot-email"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Poppins, sans-serif',
                  },
                  '& .MuiOutlinedInput-input': {
                    fontFamily: 'Poppins, sans-serif',
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleCloseForgotPasswordDialog}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: '#6B6B6B'
            }}
          >
            {resetEmailSent ? 'Close' : 'Cancel'}
          </Button>
          {!resetEmailSent && (
            <Button
              onClick={handleSendResetEmail}
              variant="contained"
              disabled={isSendingResetEmail || !emailRegex.test(forgotPasswordEmail)}
              sx={{
                background: '#3B82F6',
                fontFamily: 'Poppins, sans-serif',
                borderRadius: '8px',
                textTransform: 'none',
                minWidth: '120px'
              }}
            >
              {isSendingResetEmail ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                'Send Reset Link'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog (when user comes from email link) */}
      <Dialog
        open={showPasswordResetDialog}
        onClose={handleClosePasswordResetDialog}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          Set New Password
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'Poppins, sans-serif', marginBottom: 2 }}>
            Please enter your new password below.
          </DialogContentText>
          
          <TextField
            margin="dense"
            id="new-password"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{
              marginBottom: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
              },
              '& .MuiOutlinedInput-input': {
                fontFamily: 'Poppins, sans-serif',
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="dense"
            id="confirm-new-password"
            label="Confirm New Password"
            type={showConfirmNewPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
              },
              '& .MuiOutlinedInput-input': {
                fontFamily: 'Poppins, sans-serif',
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    edge="end"
                  >
                    {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
            <div style={{ 
              color: '#ef4444', 
              fontSize: '12px', 
              marginTop: '8px',
              fontFamily: 'Poppins, sans-serif'
            }}>
              Passwords do not match
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleClosePasswordResetDialog}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: '#6B6B6B'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordReset}
            variant="contained"
            disabled={
              isResettingPassword || 
              !newPassword || 
              !confirmNewPassword || 
              newPassword !== confirmNewPassword ||
              newPassword.length < 6
            }
            sx={{
              background: '#3B82F6',
              fontFamily: 'Poppins, sans-serif',
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: '120px'
            }}
          >
            {isResettingPassword ? (
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
            ) : (
              'Reset Password'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default LogIn;